import { useMemo } from 'react'
import { CharacterMood } from '@tfa/shared'

interface CharacterProps {
  stage: number
  mood: CharacterMood | `${CharacterMood}`
  size?: 'small' | 'medium' | 'large' | number
  animated?: boolean
  onClick?: () => void
  avatar?: boolean
}

const MOOD_FACES: Record<string, string> = {
  [CharacterMood.HAPPY]: '😊',
  [CharacterMood.SAD]: '😢',
  [CharacterMood.ANGRY]: '😤',
  [CharacterMood.SLEEPY]: '😴',
  [CharacterMood.EXCITED]: '🤩',
}

const STAGE_COLORS: Record<number, [string, string]> = {
  1: ['#6C5CE7', '#A29BFE'],
  2: ['#4A9FE5', '#74B9FF'],
  3: ['#34C759', '#55EFC4'],
  4: ['#FF9500', '#FDCB6E'],
  5: ['#FF3B30', '#FD79A8'],
}

const SIZE_MAP: Record<string, number> = {
  small: 64,
  medium: 120,
  large: 196,
}

export function Character({ stage, mood, size = 'medium', animated = true, onClick, avatar }: CharacterProps) {
  const px = typeof size === 'number' ? size : (SIZE_MAP[size] ?? 120)
  const colors = useMemo(() => STAGE_COLORS[stage] || STAGE_COLORS[1], [stage])
  const moodFace = MOOD_FACES[mood] || MOOD_FACES[CharacterMood.HAPPY]
  const faceSize = Math.max(20, px * 0.35)

  return (
    <div
      onClick={onClick}
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
      style={{
        width: px,
        height: px,
        borderRadius: avatar ? '50%' : px * 0.28,
        background: `radial-gradient(circle at 50% 40%, ${colors[1]}40, ${colors[0]}20)`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: onClick ? 'pointer' : 'default',
        position: 'relative',
        overflow: avatar ? 'hidden' : undefined,
        animation: animated && !avatar ? 'character-float 3s ease-in-out infinite' : undefined,
      }}
    >
      {/* Inner glow */}
      <div style={{
        position: 'absolute',
        width: px * 0.7,
        height: px * 0.7,
        borderRadius: '50%',
        background: `radial-gradient(ellipse, rgba(255,255,255,0.8) 0%, transparent 70%)`,
        filter: 'blur(10px)',
        pointerEvents: 'none',
      }} />
      {/* Stage body */}
      <div style={{
        position: 'relative',
        width: px * 0.55,
        height: px * 0.55,
        borderRadius: '50%',
        background: `linear-gradient(135deg, ${colors[0]}, ${colors[1]})`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        boxShadow: `0 ${px * 0.04}px ${px * 0.15}px ${colors[0]}50`,
      }}>
        <span style={{ fontSize: faceSize, lineHeight: 1, userSelect: 'none' }}>{moodFace}</span>
      </div>
      <style>{`
        @keyframes character-float {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-6px); }
        }
      `}</style>
    </div>
  )
}
